import { useEffect, useRef } from "react";

import { usePostHog } from "posthog-js/react";
import { usePubNub } from "pubnub-react";
import { useLocation } from "react-router-dom";

import { ClientAppEvent } from "constants/index";
import { useChatUnreadContext } from "context/ChatUnreadContext";
import { useAPI, useAuthContext, useCurrentlyOpenChatChannel } from "hooks";

import {
  inboxChatFromMessageEnvelopeOrNull,
  payloadIsAttachment,
} from "./utils";

import type { MessageEnvelope } from "@pubnub/react-chat-components";

import type { InboxChat } from "context/ChatUnreadContext";

interface ChatInboxResponse {
  unread_chats: InboxChat[];
  read_chats: InboxChat[];
  truncated: boolean;
}

/**
 * App-level component that drives ChatUnreadContext from PubNub.
 *
 * - Subscribes to the user's PubNub channel group on mount, unsubscribes on
 *   unmount.
 * - Seeds the inbox once via GET /user/chats/inbox so unread state is correct
 *   after refresh.
 * - On every incoming message, if it's not for the channel currently open in
 *   the URL, applies it to the inbox.
 * - Re-seeds when PubNub reconnects to close the disconnect-window gap.
 *
 * Renders nothing — purely a side-effect component.
 */
export function ChatRealtimeListener() {
  const { pubnub: pubnubUserInfo } = useAuthContext();
  const channelGroup = pubnubUserInfo?.channel_group_name;
  const pubnubClient = usePubNub();
  const { api } = useAPI();
  const posthog = usePostHog();
  const { seed, applyIncomingMessage, hasAnyUnread } = useChatUnreadContext();
  const currentlyOpenChannel = useCurrentlyOpenChatChannel();
  const { pathname } = useLocation();

  // Hold the latest values in refs so the message handler reads them without
  // forcing the listener to re-subscribe on every route change. Re-subscribing
  // would issue redundant inbox seeds and open a small race window where a
  // message published between unsubscribe/subscribe goes unobserved.
  const currentlyOpenChannelRef = useRef<string | null>(currentlyOpenChannel);
  const pathnameRef = useRef<string>(pathname);
  // Mirror hasAnyUnread so the message handler can detect the off→on
  // transition for the was_first_unread analytic without re-subscribing.
  const hasAnyUnreadRef = useRef<boolean>(hasAnyUnread);
  useEffect(() => {
    currentlyOpenChannelRef.current = currentlyOpenChannel;
    pathnameRef.current = pathname;
    hasAnyUnreadRef.current = hasAnyUnread;
  }, [currentlyOpenChannel, pathname, hasAnyUnread]);

  useEffect(() => {
    if (!channelGroup) return undefined;

    const seedInbox = async () => {
      try {
        const response = await api<ChatInboxResponse>(
          "get",
          "/user/chats/inbox",
        );
        seed(
          [...response.data.unread_chats, ...response.data.read_chats],
          response.data.truncated,
        );
      } catch (error) {
        // 503 from upstream PubNub failure: leave whatever live state we have
        // and rely on the next reconnect or live message to recover.
        // eslint-disable-next-line no-console
        console.warn("Failed to seed chat inbox", error);
      }
    };

    seedInbox();

    const listener = {
      message: (envelope: MessageEnvelope) => {
        if (envelope.channel === currentlyOpenChannelRef.current) return;
        const row = inboxChatFromMessageEnvelopeOrNull(envelope);
        if (row === null) return;

        const wasFirstUnread = !hasAnyUnreadRef.current;
        applyIncomingMessage(row);

        posthog?.capture(ClientAppEvent.CHAT_INBOX_UNREAD_RECEIVED, {
          channel_name: row.channel_name,
          shipment_id: row.shipment_id,
          is_attachment: payloadIsAttachment(envelope.message ?? {}),
          path: pathnameRef.current,
          was_first_unread: wasFirstUnread,
        });
      },
      status: (event: { category: string }) => {
        if (
          event.category === "PNReconnectedCategory" ||
          event.category === "PNConnectedCategory"
        ) {
          seedInbox();
        }
      },
    };

    pubnubClient.addListener(listener);
    pubnubClient.subscribe({
      channelGroups: [channelGroup],
      withPresence: false,
    });

    return () => {
      pubnubClient.removeListener(listener);
      pubnubClient.unsubscribe({ channelGroups: [channelGroup] });
    };
  }, [pubnubClient, channelGroup, api, seed, applyIncomingMessage, posthog]);

  return null;
}
